import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  IconButton,
  Avatar,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Autocomplete,
  LinearProgress,
  Stack,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  PhotoCamera,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { CreateAnimalRequest } from '@services/animalsService';
import littersService from '@services/littersService';
import { compressImage, validateImageType, formatFileSize, createImagePreview } from '@utils/imageCompression';
import { saveDraft, loadDraft, deleteDraft, hasDraft, getDraftTimestamp } from '@utils/formDraftStorage';
import { SPECIES, getBreedsBySpecies, getCommonBreeds } from '@constants/animalData';
import { useAuth } from '@contexts/AuthContext';
import { Animal } from '@services/animalsService';

/**
 * Task #7, #8, #9, #10, #11: Dialog imbunatatit pentru creare/editare animal
 * - Erori pe camp si helper text (Task #7)
 * - Disable + loader pe buton (Task #8)
 * - Preview + compresie imagine (Task #9)
 * - Liste controlate specie/rasa (Task #10)
 * - Autosave draft (Task #11)
 */

interface AnimalFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  animal?: Animal | null;
  formData: CreateAnimalRequest;
  setFormData: (data: CreateAnimalRequest) => void;
  formErrors: Record<string, string>;
  isLoading: boolean;
}

const FORM_DRAFT_ID = 'animal_form';
const AUTOSAVE_DELAY = 500; // ms

const AnimalFormDialog: React.FC<AnimalFormDialogProps> = ({
  open,
  onClose,
  onSave,
  animal,
  formData,
  setFormData,
  formErrors,
  isLoading
}) => {
  const { user } = useAuth();
  
  // Determine if we're editing an existing animal
  const isEditing = !!animal;

  // Task #7: Erori pe camp (foloseste formErrors din props)
  const fieldErrors = formErrors;
  
  // Task #8: Loading state pentru buton (foloseste isLoading din props)
  const isSaving = isLoading;
  
  // Task #9: Preview imagine
  const [imagePreview, setImagePreview] = useState<string | null>(formData.profileImage || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState<{
    originalSize: number;
    compressedSize: number;
    ratio: number;
  } | null>(null);

  // Microchip validation state - if editing, consider microchip already validated
  const [microchipValidated, setMicrochipValidated] = useState(isEditing);
  const [validatingMicrochip, setValidatingMicrochip] = useState(false);
  const [microchipValidationMessage, setMicrochipValidationMessage] = useState('');

  // Task #11: Draft restoration
  const [draftAvailable, setDraftAvailable] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState<Date | null>(null);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);

  // Autosave timer
  const [autosaveTimer, setAutosaveTimer] = useState<NodeJS.Timeout | null>(null);

  // Custom breed input
  const [customBreed, setCustomBreed] = useState('');
  const [useCustomBreed, setUseCustomBreed] = useState(false);

  // Initialize image preview when formData changes
  useEffect(() => {
    if (formData.profileImage) {
      setImagePreview(formData.profileImage);
    }
  }, [formData.profileImage]);

  // Update microchip validation when dialog opens for editing
  useEffect(() => {
    if (open && isEditing) {
      setMicrochipValidated(true);
      setMicrochipValidationMessage('Microchip valid (animal existent)');
    } else if (open && !isEditing) {
      setMicrochipValidated(false);
      setMicrochipValidationMessage('');
    }
  }, [open, isEditing]);

  // Task #11: Autosave draft
  const scheduleAutosave = useCallback(() => {
    if (autosaveTimer) {
      clearTimeout(autosaveTimer);
    }

    const timer = setTimeout(() => {
      if (!isEditing) {
        saveDraft(FORM_DRAFT_ID, formData, user?.id);
      }
    }, AUTOSAVE_DELAY);

    setAutosaveTimer(timer);
  }, [formData, isEditing, user?.id, autosaveTimer]);

  // Trigger autosave when form data changes (only for new animals)
  useEffect(() => {
    if (open && !animal && formData.name) {
      scheduleAutosave();
    }
  }, [formData, open, animal]);

  // Cleanup autosave timer
  useEffect(() => {
    return () => {
      if (autosaveTimer) {
        clearTimeout(autosaveTimer);
      }
    };
  }, [autosaveTimer]);

  // Task #11: Load draft
  const handleLoadDraft = () => {
    const draft = loadDraft<CreateAnimalRequest>(FORM_DRAFT_ID, user?.id);
    if (draft) {
      setFormData(draft);
      if (draft.profileImage) {
        setImagePreview(draft.profileImage);
      }
    }
    setShowDraftPrompt(false);
  };

  // Task #11: Discard draft
  const handleDiscardDraft = () => {
    deleteDraft(FORM_DRAFT_ID);
    setShowDraftPrompt(false);
  };

  // Microchip validation function
  const validateMicrochip = async (microchip: string) => {
    if (!microchip) {
      setMicrochipValidationMessage('Microchip-ul este obligatoriu');
      return false;
    }
    
    // Check if numeric and length
    if (!/^\d+$/.test(microchip)) {
      setMicrochipValidationMessage('Microchip-ul trebuie sa contina doar cifre');
      return false;
    }
    
    if (microchip.length < 6 || microchip.length > 20) {
      setMicrochipValidationMessage('Microchip-ul trebuie sa aiba intre 6 si 20 de cifre');
      return false;
    }
    
    // Skip uniqueness check if editing and microchip hasn't changed
    if (isEditing && animal?.microchipId === microchip) {
      setMicrochipValidationMessage('Microchip valid (nemodificat)');
      setMicrochipValidated(true);
      return true;
    }
    
    // Check uniqueness
    try {
      setValidatingMicrochip(true);
      const result = await littersService.checkMicrochipUnique(microchip);
      
      if (!result.unique) {
        setMicrochipValidationMessage(`Microchip-ul ${microchip} este deja folosit`);
        return false;
      }
      
      setMicrochipValidationMessage('Microchip valid si disponibil');
      setMicrochipValidated(true);
      return true;
    } catch (error) {
      setMicrochipValidationMessage('Eroare la verificarea microchip-ului');
      return false;
    } finally {
      setValidatingMicrochip(false);
    }
  };
  
  const handleMicrochipChange = (value: string) => {
    handleFieldChange('microchipId', value);
  };
  
  const handleMicrochipBlur = async () => {
    if (formData.microchipId) {
      await validateMicrochip(formData.microchipId);
    }
  };

  // Task #7: Validate field
  const validateField = (name: string, value: any): string | null => {
    switch (name) {
      case 'name':
        if (!value || value.trim().length < 2) {
          return 'Numele trebuie sa aiba cel putin 2 caractere';
        }
        if (value.trim().length > 100) {
          return 'Numele nu poate depasi 100 de caractere';
        }
        return null;

      case 'species':
        if (!value) {
          return 'Specia este obligatorie';
        }
        return null;

      case 'breed':
        if (!value || value.trim().length < 2) {
          return 'Rasa este obligatorie';
        }
        return null;

      case 'gender':
        if (!value) {
          return 'Sexul este obligatoriu';
        }
        return null;

      case 'weight':
        if (value !== undefined && value !== null) {
          if (value <= 0) {
            return 'Greutatea trebuie sa fie mai mare de 0';
          }
          if (value > 1000) {
            return 'Greutatea pare neobisnuit de mare';
          }
        }
        return null;

      case 'birthDate':
        if (value) {
          const birthDate = new Date(value);
          const today = new Date();
          if (birthDate > today) {
            return 'Data nasterii nu poate fi in viitor';
          }
        }
        return null;

      case 'microchipId':
        if (value && value.length < 10) {
          return 'Microchip-ul trebuie sa aiba cel putin 10 caractere';
        }
        return null;

      default:
        return null;
    }
  };

  // Task #7: Handle field change with validation
  const handleFieldChange = (name: string, value: any) => {
    setFormData({ ...formData, [name]: value });

    // Task #10: Clear breed when species changes
    if (name === 'species') {
      setFormData({ ...formData, species: value, breed: '' });
      setUseCustomBreed(false);
      setCustomBreed('');
    }

    // Reset microchip validation if microchip changes
    if (name === 'microchipId') {
      setMicrochipValidated(false);
      setMicrochipValidationMessage('');
    }
  };

  // Task #9: Handle image upload with compression
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!validateImageType(file)) {
      console.error('Invalid file type');
      return;
    }

    setIsCompressing(true);

    try {
      // Compress image
      const result = await compressImage(file, {
        maxWidth: 800,
        maxHeight: 800,
        quality: 0.85,
        maxSizeInMB: 5,
      });

      setSelectedFile(result.file);
      setImagePreview(result.preview);
      setCompressionInfo({
        originalSize: result.originalSize,
        compressedSize: result.compressedSize,
        ratio: result.compressionRatio,
      });

      // Store preview in form data (URL will be set after upload to server)
      setFormData({ ...formData, profileImage: result.preview });
    } catch (error: any) {
      console.error('Error processing image:', error);
    } finally {
      setIsCompressing(false);
    }
  };

  // Task #9: Remove image
  const handleRemoveImage = () => {
    setImagePreview(null);
    setSelectedFile(null);
    setCompressionInfo(null);
    setFormData({ ...formData, profileImage: undefined });
  };

  // Task #7 & #8: Validate and save
  const handleSave = async () => {
    // Validate all fields
    const errors: { [key: string]: string } = {};
    
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, (formData as any)[key]);
      if (error) {
        errors[key] = error;
      }
    });

    // Check required fields
    if (!formData.name) errors.name = 'Numele este obligatoriu';
    if (!formData.species) errors.species = 'Specia este obligatorie';
    if (!formData.breed) errors.breed = 'Rasa este obligatorie';
    if (!formData.gender) errors.gender = 'Sexul este obligatoriu';
    if (!formData.microchipId) errors.microchipId = 'Microchip-ul este obligatoriu';
    if (!microchipValidated) errors.microchipId = 'Microchip-ul trebuie validat inainte de salvare';

    if (Object.keys(errors).length > 0) {
      // Focus on first error field
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement;
      element?.focus();
      
      return;
    }

    try {
      // Update formData if using custom breed
      if (useCustomBreed && customBreed) {
        setFormData({
          ...formData,
          breed: customBreed,
        });
      }

      await onSave();
      
      // Task #11: Delete draft after successful save (only for new animals)
      if (!animal) {
        deleteDraft(FORM_DRAFT_ID);
      }

      handleClose();
    } catch (error: any) {
      // Errors are handled by parent component
      console.error('Error saving animal:', error);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setFormData({
        name: '',
        species: undefined,
        breed: '',
        gender: undefined,
        color: '',
        weight: undefined,
        microchipId: '',
        birthDate: undefined,
        profileImage: undefined,
        reproduced: false,
      });
      // Errors are handled by parent component
      setImagePreview(null);
      setSelectedFile(null);
      setCompressionInfo(null);
      setUseCustomBreed(false);
      setCustomBreed('');
      setMicrochipValidated(false);
      setMicrochipValidationMessage('');
      onClose();
    }
  };

  // Get breeds for selected species
  const availableBreeds = formData.species ? getBreedsBySpecies(formData.species) : [];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={isSaving}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {isEditing ? 'Editeaza Animal' : 'Adauga Animal Nou'}
          </Typography>
          <IconButton onClick={handleClose} disabled={isSaving}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Task #11: Draft restoration prompt */}
        {showDraftPrompt && draftAvailable && (
          <Alert
            severity="info"
            action={
              <Stack direction="row" spacing={1}>
                <Button size="small" color="inherit" onClick={handleLoadDraft}>
                  Restaureaza
                </Button>
                <Button size="small" color="inherit" onClick={handleDiscardDraft}>
                  Sterge
                </Button>
              </Stack>
            }
            sx={{ mb: 2 }}
          >
            Am gasit un draft salvat{' '}
            {draftTimestamp && `la ${draftTimestamp.toLocaleString('ro-RO')}`}.
            Vrei sa il restaurezi?
          </Alert>
        )}

        {/* Task #7: General error */}
        {fieldErrors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {fieldErrors.general}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Microchip ID - First field with validation */}
          <Box>
            <TextField
              fullWidth
              required
              name="microchipId"
              label="Microchip ID *"
              value={formData.microchipId || ''}
              onChange={(e) => handleMicrochipChange(e.target.value)}
              onBlur={handleMicrochipBlur}
              error={!!fieldErrors.microchipId || (!!formData.microchipId && !microchipValidated)}
              helperText={
                fieldErrors.microchipId || 
                microchipValidationMessage || 
                'Numarul microchipului (6-20 cifre, obligatoriu)'
              }
              disabled={isSaving || validatingMicrochip}
              InputProps={{
                endAdornment: validatingMicrochip ? (
                  <CircularProgress size={20} />
                ) : microchipValidated ? (
                  <CheckCircleIcon color="success" />
                ) : null
              }}
            />
            
            {/* Validation status chip */}
            {formData.microchipId && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  icon={microchipValidated ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={microchipValidated ? 'Microchip valid' : 'Microchip invalid'}
                  color={microchipValidated ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            )}
          </Box>

          {/* Rest of fields - disabled until microchip is validated */}
          <Box sx={{ opacity: microchipValidated ? 1 : 0.6 }}>
            {/* Task #9: Image preview and upload */}
            <Box>
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Avatar
                  src={imagePreview || undefined}
                  sx={{ width: 120, height: 120, bgcolor: 'primary.main' }}
                >
                  {formData.name?.charAt(0)?.toUpperCase() || '🐾'}
                </Avatar>

                {isCompressing && (
                  <Box sx={{ width: '100%', maxWidth: 300 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Se comprima imaginea...
                    </Typography>
                    <LinearProgress />
                  </Box>
                )}

                {compressionInfo && (
                  <Box textAlign="center">
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`Comprimat: ${formatFileSize(compressionInfo.originalSize)} → ${formatFileSize(compressionInfo.compressedSize)} (${compressionInfo.ratio}% reducere)`}
                      color="success"
                      size="small"
                    />
                  </Box>
                )}

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<PhotoCamera />}
                    disabled={isSaving || isCompressing || !microchipValidated}
                  >
                    {imagePreview ? 'Schimba Poza' : 'Adauga Poza'}
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleImageUpload}
                    />
                  </Button>

                  {imagePreview && (
                    <IconButton
                      color="error"
                      onClick={handleRemoveImage}
                      disabled={isSaving || isCompressing || !microchipValidated}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Stack>

                {/* Task #7: Image error */}
                {fieldErrors.profileImage && (
                  <Typography variant="caption" color="error">
                    {fieldErrors.profileImage}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Task #7: Name field with error */}
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                required
                name="name"
                label="Nume"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || 'Numele animalului'}
                disabled={isSaving || !microchipValidated}
              />
            </Box>

            {/* Task #10: Species select with controlled list */}
            <Box sx={{ mt: 3 }}>
              <FormControl fullWidth required error={!!fieldErrors.species}>
                <InputLabel>Specie</InputLabel>
                <Select
                  name="species"
                  value={formData.species || ''}
                  onChange={(e) => handleFieldChange('species', e.target.value)}
                  label="Specie"
                  disabled={isSaving || !microchipValidated}
                >
                  {SPECIES.map((species) => (
                    <MenuItem key={species.value} value={species.value}>
                      {species.icon} {species.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {fieldErrors.species || 'Selecteaza specia animalului'}
                </FormHelperText>
              </FormControl>
            </Box>

            {/* Task #10: Breed autocomplete with common breeds */}
            <Box sx={{ mt: 3 }}>
              {useCustomBreed ? (
                <TextField
                  fullWidth
                  required
                  label="Rasa Personalizata"
                  value={customBreed}
                  onChange={(e) => setCustomBreed(e.target.value)}
                  error={!!fieldErrors.breed}
                  helperText={fieldErrors.breed || 'Introdu rasa animalului'}
                  disabled={isSaving || !formData.species || !microchipValidated}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setUseCustomBreed(false);
                          setCustomBreed('');
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    ),
                  }}
                />
              ) : (
                <Autocomplete
                  options={availableBreeds}
                  getOptionLabel={(option) => option.label}
                  value={availableBreeds.find((b) => b.value === formData.breed) || null}
                  onChange={(_, newValue) => {
                    if (newValue?.value === 'other') {
                      setUseCustomBreed(true);
                    } else {
                      handleFieldChange('breed', newValue?.value || '');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Rasa"
                      error={!!fieldErrors.breed}
                      helperText={fieldErrors.breed || 'Selecteaza sau cauta rasa'}
                      disabled={isSaving || !formData.species || !microchipValidated}
                    />
                  )}
                  disabled={isSaving || !formData.species || !microchipValidated}
                  noOptionsText="Nu s-au gasit rase"
                />
              )}
            </Box>

            {/* Gender */}
            <Box sx={{ mt: 3 }}>
              <FormControl fullWidth required error={!!fieldErrors.gender}>
                <InputLabel>Sex</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender || ''}
                  onChange={(e) => handleFieldChange('gender', e.target.value)}
                  label="Sex"
                  disabled={isSaving || !microchipValidated}
                >
                  <MenuItem value="male">Mascul</MenuItem>
                  <MenuItem value="female">Femela</MenuItem>
                </Select>
                <FormHelperText>
                  {fieldErrors.gender || 'Selecteaza sexul animalului'}
                </FormHelperText>
              </FormControl>
            </Box>

            {/* Birth date */}
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                name="birthDate"
                label="Data Nasterii"
                type="date"
                value={formData.birthDate || ''}
                onChange={(e) => handleFieldChange('birthDate', e.target.value)}
                error={!!fieldErrors.birthDate}
                helperText={fieldErrors.birthDate || 'Data nasterii animalului'}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split('T')[0] }}
                disabled={isSaving || !microchipValidated}
              />
            </Box>

            {/* Weight */}
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                name="weight"
                label="Greutate (kg)"
                type="number"
                value={formData.weight || ''}
                onChange={(e) => handleFieldChange('weight', parseFloat(e.target.value) || undefined)}
                error={!!fieldErrors.weight}
                helperText={fieldErrors.weight || 'Greutatea in kilograme'}
                inputProps={{ step: 0.1, min: 0, max: 1000 }}
                disabled={isSaving || !microchipValidated}
              />
            </Box>

            {/* Color */}
            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                name="color"
                label="Culoare"
                value={formData.color || ''}
                onChange={(e) => handleFieldChange('color', e.target.value)}
                error={!!fieldErrors.color}
                helperText={fieldErrors.color || 'Culoarea blanii/penajului'}
                disabled={isSaving || !microchipValidated}
              />
            </Box>

            {/* Reproducere section */}
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Reproducere
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.reproduced || false}
                    onChange={(e) => handleFieldChange('reproduced', e.target.checked)}
                    disabled={isSaving || !microchipValidated}
                    name="reproduced"
                  />
                }
                label="S-a reprodus?"
              />
              <FormHelperText sx={{ mt: 1, ml: 0 }}>
                Selecteaza 'Da' daca acest animal are cel putin o fatare in istoric.
                Adaugarea fatarilor se face ulterior, din cardul animalului.
              </FormHelperText>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isSaving}>
          Anuleaza
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || !microchipValidated}
          startIcon={isSaving && <CircularProgress size={20} />}
        >
          {isSaving ? 'Se salveaza...' : 'Salveaza'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnimalFormDialog;

